import React from 'react';
import { FaMinus, FaPlus } from 'react-icons/fa';
import {
  PlanTier, BillingInterval, CatalogAddOn, EstimateResponse, parseDecimal,
} from '../services/BillingService';
import { personalakteStyles as ps, colors } from '../../employees/styles/personalakteStyles';
import CostSummary from './CostSummary';

interface BillingConfiguratorProps {
  selectedTier: PlanTier;
  billingInterval: BillingInterval;
  yearlyAvailable: boolean;
  locationCount: number;
  extraPacks: number;
  addOns: CatalogAddOn[];
  enabledAddonSlugs: string[];
  extraPackInfo: { size: number; price_monthly: string } | null;
  estimate: EstimateResponse | null;
  estimateLoading: boolean;
  onBillingIntervalChange: (interval: BillingInterval) => void;
  onLocationCountChange: (count: number) => void;
  onExtraPacksChange: (packs: number) => void;
  onToggleAddOn: (slug: string) => void;
  onApply: () => void;
  applyLabel: string;
}

function Stepper({
  label,
  sublabel,
  value,
  min,
  onDecrement,
  onIncrement,
}: {
  label: string;
  sublabel: string;
  value: number;
  min: number;
  onDecrement: () => void;
  onIncrement: () => void;
}): React.JSX.Element {
  const canDecrement = value > min;

  return (
    <div style={{ ...ps.cardCompact, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: colors.navy, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 12, color: colors.textMuted }}>{sublabel}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onDecrement}
          disabled={!canDecrement}
          style={{
            width: 32, height: 32, borderRadius: '50%', border: `1px solid ${colors.border}`,
            background: colors.bgCard, cursor: canDecrement ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: colors.textMuted, opacity: canDecrement ? 1 : 0.4,
          }}
        >
          <FaMinus size={10} />
        </button>
        <span style={{ fontSize: 18, fontWeight: 700, color: colors.navy, minWidth: 24, textAlign: 'center' }}>
          {value}
        </span>
        <button
          onClick={onIncrement}
          style={{
            width: 32, height: 32, borderRadius: '50%', border: `1px solid ${colors.border}`,
            background: colors.bgCard, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textMuted,
          }}
        >
          <FaPlus size={10} />
        </button>
      </div>
    </div>
  );
}

function ToggleSwitch({ enabled, onToggle }: { enabled: boolean; onToggle: () => void }): React.JSX.Element {
  return (
    <button
      onClick={onToggle}
      style={{
        width: 44, height: 24, borderRadius: 12, border: 'none',
        background: enabled
          ? 'linear-gradient(90deg, #0E1333 0%, #E7007D 100%)'
          : colors.border,
        cursor: 'pointer', position: 'relative', transition: 'background 0.2s ease', flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          position: 'absolute', top: 3, left: enabled ? 23 : 3,
          transition: 'left 0.2s ease', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );
}

function AddOnTile({
  addon,
  enabled,
  onToggle,
}: {
  addon: CatalogAddOn;
  enabled: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const price = parseDecimal(addon.price_monthly);
  const billingLabel = addon.billing_type === 'per_location'
    ? `${price.toFixed(0)} € / Standort / Monat`
    : `${price.toFixed(0)} € / Monat`;

  return (
    <div
      style={{
        ...ps.cardCompact, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <div style={{ flex: 1, marginRight: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: colors.navy, marginBottom: 2 }}>
          {addon.name}
        </div>
        <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 2 }}>{billingLabel}</div>
        <div style={{ fontSize: 12, color: colors.textBody, marginTop: 4 }}>{addon.description}</div>
      </div>

      <ToggleSwitch enabled={enabled} onToggle={onToggle} />
    </div>
  );
}

export default function BillingConfigurator({
  selectedTier,
  billingInterval,
  yearlyAvailable,
  locationCount,
  extraPacks,
  addOns,
  enabledAddonSlugs,
  extraPackInfo,
  estimate,
  estimateLoading,
  onBillingIntervalChange,
  onLocationCountChange,
  onExtraPacksChange,
  onToggleAddOn,
  onApply,
  applyLabel,
}: BillingConfiguratorProps): React.JSX.Element {
  const perLocationAddOns = addOns.filter((a) => a.billing_type === 'per_location');
  const perTenantAddOns = addOns.filter((a) => a.billing_type === 'per_tenant');

  const packSize = extraPackInfo?.size ?? 5;
  const packPrice = extraPackInfo ? parseDecimal(extraPackInfo.price_monthly) : 25;

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={ps.sectionHeader}>
        <div style={ps.sectionDot(colors.slate)} />
        Konfiguration
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20, alignItems: 'start' }}>
        {/* Left Column — Controls */}
        <div>
          {/* Billing Interval Toggle — only if yearly available */}
          {yearlyAvailable && (
            <div
              style={{
                display: 'inline-flex', background: colors.bgMuted, borderRadius: 9999,
                padding: 3, marginBottom: 20, border: `1px solid ${colors.border}`,
              }}
            >
              {(['monthly', 'yearly'] as BillingInterval[]).map((interval) => {
                const isActive = billingInterval === interval;
                return (
                  <button
                    key={interval}
                    onClick={() => onBillingIntervalChange(interval)}
                    style={{
                      padding: '8px 20px', borderRadius: 9999, border: 'none', cursor: 'pointer',
                      fontSize: 13, fontWeight: isActive ? 600 : 500,
                      color: isActive ? '#fff' : colors.textMuted,
                      background: isActive
                        ? 'linear-gradient(90deg, #0E1333 0%, #0E1333 30%, #E7007D 100%)'
                        : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {interval === 'monthly' ? 'Monatlich' : 'Jährlich (−15 %)'}
                  </button>
                );
              })}
            </div>
          )}

          {/* Standorte + Extra MA */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            <Stepper
              label="Standorte"
              sublabel="Anzahl gebuchter Standorte"
              value={locationCount}
              min={1}
              onDecrement={() => onLocationCountChange(Math.max(1, locationCount - 1))}
              onIncrement={() => onLocationCountChange(locationCount + 1)}
            />

            <Stepper
              label="Extra Mitarbeitende"
              sublabel={`+${packSize} pro Paket · ${packPrice.toFixed(0)} €/Monat`}
              value={extraPacks}
              min={0}
              onDecrement={() => onExtraPacksChange(Math.max(0, extraPacks - 1))}
              onIncrement={() => onExtraPacksChange(extraPacks + 1)}
            />
          </div>

          {/* Add-ons: Per Location */}
          {perLocationAddOns.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.textLight, textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 8 }}>
                Pro Standort
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {perLocationAddOns.map((addon) => (
                  <AddOnTile
                    key={addon.slug}
                    addon={addon}
                    enabled={enabledAddonSlugs.includes(addon.slug)}
                    onToggle={() => onToggleAddOn(addon.slug)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Add-ons: Per Tenant */}
          {perTenantAddOns.length > 0 && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.textLight, textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 8 }}>
                Pro Tenant
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {perTenantAddOns.map((addon) => (
                  <AddOnTile
                    key={addon.slug}
                    addon={addon}
                    enabled={enabledAddonSlugs.includes(addon.slug)}
                    onToggle={() => onToggleAddOn(addon.slug)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column — Cost Summary (sticky) */}
        <CostSummary
          estimate={estimate}
          estimateLoading={estimateLoading}
          onApply={onApply}
          applyLabel={applyLabel}
        />
      </div>
    </div>
  );
}
